"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.quickShip = quickShip;
const weavePlan_1 = require("./weavePlan");
const completeStep_1 = require("./completeStep");
const closePlan_1 = require("./closePlan");
const thread_1 = require("./thread");
const runEvent_1 = require("./runEvent");
/** Normalize description (string | string[]) into a validated non-empty list of step lines. */
function normalizeDescriptions(raw) {
    const list = Array.isArray(raw) ? raw : [raw];
    const cleaned = list
        .map(d => (typeof d === 'string' ? d.trim() : ''))
        .filter(d => d !== '');
    if (cleaned.length === 0) {
        throw new Error('loom_quick_ship: `description` must be a non-empty string or a non-empty array of non-empty strings.');
    }
    return cleaned;
}
async function quickShip(input, deps) {
    // Target selection: exactly one of an existing threadUlid or a newThread to mint.
    const hasThreadUlid = typeof input.threadUlid === 'string' && input.threadUlid.trim() !== '';
    const hasNewThread = !!input.newThread && typeof input.newThread.slug === 'string' && input.newThread.slug.trim() !== '';
    if (hasThreadUlid === hasNewThread) {
        throw new Error('loom_quick_ship: pass exactly one of `thread_ulid` (existing thread) or `newThread` (mint a thread).');
    }
    const descriptions = normalizeDescriptions(input.description);
    // createThread keys on getActiveLoomRoot(); quick-ship runs against a fixed root.
    const getActiveLoomRoot = () => deps.loomRoot;
    let createdThread = false;
    // Downstream create use-cases reference the thread by its stable th_ ULID.
    // A minted thread yields its ULID; an existing target is passed by ULID.
    let threadUlid;
    if (hasNewThread) {
        const slug = input.newThread.slug.trim();
        const { id } = await (0, thread_1.createThread)({ weaveSlug: input.weaveSlug, threadSlug: slug, title: input.newThread.title }, { getActiveLoomRoot, saveDoc: deps.saveDoc, fs: deps.fs });
        threadUlid = id;
        createdThread = true;
    }
    else {
        threadUlid = input.threadUlid.trim();
    }
    // 1. Create the plan — steps = the descriptions (born status "active").
    const goal = descriptions.length === 1
        ? descriptions[0]
        : `Quick-ship record of ${descriptions.length} completed changes.`;
    const { id: planId, filePath } = await (0, weavePlan_1.weavePlan)({
        weaveSlug: input.weaveSlug,
        threadUlid: threadUlid,
        goal,
        steps: descriptions.map(d => ({ description: d })),
    }, {
        loadWeave: deps.loadWeave,
        saveDoc: deps.saveDoc,
        loadDoc: deps.loadDoc,
        fs: deps.fs,
        loomRoot: deps.loomRoot,
    });
    // 2. Start the plan (active → implementing) so its steps can be completed.
    await (0, runEvent_1.runEvent)(input.weaveSlug, { type: 'START_IMPLEMENTING_PLAN', planId }, {
        loadWeave: deps.loadWeave,
        saveDocs: deps.saveDocs,
        loomRoot: deps.loomRoot,
    });
    // 3. Complete every step. Completing the last step auto-finishes the plan (→ done).
    const boundRunEvent = (weaveId, event) => (0, runEvent_1.runEvent)(weaveId, event, {
        loadWeave: deps.loadWeave,
        saveDocs: deps.saveDocs,
        loomRoot: deps.loomRoot,
    });
    for (let step = 1; step <= descriptions.length; step++) {
        await (0, completeStep_1.completeStep)({ planUlid: planId, step }, { loadWeave: deps.loadWeave, runEvent: boundRunEvent, loomRoot: deps.loomRoot });
    }
    // 4. Write the done record (closePlan also runs FINISH_PLAN if not already auto-finished).
    const notes = input.notes && input.notes.trim()
        ? input.notes.trim()
        : `Quick-shipped — recorded already-completed work:\n\n` +
            descriptions.map((d, i) => `${i + 1}. ${d}`).join('\n');
    const { donePath } = await (0, closePlan_1.closePlan)({ planUlid: planId, notes }, { loadWeave: deps.loadWeave, saveDoc: deps.saveDoc, fs: deps.fs, loomRoot: deps.loomRoot });
    return {
        planId,
        weaveSlug: input.weaveSlug,
        threadUlid: threadUlid,
        filePath,
        donePath,
        stepCount: descriptions.length,
        createdThread,
    };
}
